import { useEffect, useRef, useState } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useAppStore } from '@/stores/appStore';

interface SavedToastProps {
  /** Positioning classes supplied by the container (e.g. "absolute top-2 right-6 z-40"). */
  className?: string;
}

/**
 * Transient "Saved" confirmation pill. Listens to the store's `savedFlashToken` so any tab or
 * panel can trigger it via `flashSaved()`, and the pill is rendered/positioned by the page or
 * panel container — never inside scrolling tab content.
 */
export function SavedToast({ className }: SavedToastProps) {
  const token = useAppStore((s) => s.savedFlashToken);
  const [show, setShow] = useState(false);
  // The token persists for the whole session, so it may already be non-zero at
  // mount (e.g. after an earlier save). Only flash on a genuine increment AFTER
  // this component mounts — never on the stale mount-time value, which otherwise
  // makes the pill appear on a freshly opened Add modal.
  const mountTokenRef = useRef(token);

  useEffect(() => {
    if (token === mountTokenRef.current) return; // mount value or no change — ignore
    setShow(true);
    const t = setTimeout(() => setShow(false), 2000);
    return () => clearTimeout(t);
  }, [token]);

  return (
    <div
      className={`flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-full px-2 py-0.5 shadow-sm transition-opacity duration-300 pointer-events-none ${
        show ? 'opacity-100' : 'opacity-0'
      } ${className || ''}`}
    >
      <CheckCircleIcon className="w-4 h-4" />
      Saved
    </div>
  );
}
